<template>
  <tr
    class="bg-white hover:bg-gray-50 transition-colors duration-150 ease-in-out text-right child:py-5 first-child:pl-3 last-child:pr-3"
    @click="open = !open"
  >
    <td>
      <div class="flex items-center space-x-1">
        <button
          data-test="open-claim"
          type="button"
          class="btn-icon btn-icon-default"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
            :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <div class="flex items-start space-x-2 text-left">
          <span class="font-semibold text-gray-900">{{ $props.trade.billNumber }}</span>

          <p class="w-60">
            {{ trade.name }}
          </p>
        </div>
      </div>
    </td>

    <td class="pr-3">
      <div v-if="totalCosts.budget !== 0">
        <span
          v-if="totalCosts.budget - totalCosts.adjustedBudget !== 0"
          class="text-gray-600"
        >Works:</span> {{ $tcc(totalCosts.budget) }}
      </div>

      <div v-if="totalCosts.budget - totalCosts.adjustedBudget !== 0">
        <span
          v-if="totalCosts.budget !== 0"
          class="text-gray-600"
        >Adjustments:</span> {{ $tcc(-(totalCosts.budget - totalCosts.adjustedBudget)) }}
      </div>
    </td>

    <td class="bg-green-100/50">
      <span v-if="showSubcontractSummary">{{ $tcc(totalCosts.subcontractClaim) }}</span>
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="bg-green-100/50"
    >
      {{ hasWorks ? $tcc(totalCosts.currentClaim) : '' }}
    </td>

    <td class="bg-green-100/50">
      <span v-if="showSubcontractSummary">{{ $tcc(totalCosts.subcontractCertified) }}</span>
    </td>

    <td class="bg-green-100/50">
      {{ hasWorks ? $tcc(totalCosts.currentCertified) : '' }}
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="group bg-green-100/50"
      :class="{ 'text-red-600': hasVariance && variance !== 0 }"
      :title="hasVariance && variance !== 0 && varianceComment?.body ? varianceComment.body : ''"
    >
      <div class="flex items-center justify-end">
        <button
          v-if="showCommentButton"
          type="button"
          class="btn-icon group-hover:visible btn-icon-default invisible mr-1 shrink-0"
          aria-label="Edit variance comment"
          @click.stop="editVarianceComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <div class="min-w-0">
          {{ hasWorks ? $tcc(totalCosts.variance) : '' }}
        </div>
      </div>
    </td>

    <td class="bg-green-100/50">
      {{ hasWorks ? $tcc(totalCosts.previous) : '' }}
    </td>

    <td class="bg-green-100/50 pr-3">
      {{ hasWorks ? $tcc(totalCosts.cumulative) : '' }}
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="bg-blue-100/50"
    >
      <span v-if="showPercentages">
        {{ hasWorks && isNumber(totalCosts.currentClaim / totalCosts.budget) ? `${$tn((totalCosts.currentClaim / totalCosts.budget) * 100)}%` : '' }}
      </span>
    </td>

    <td
      v-if="!claim.capture_claim_amount || showPercentages"
      class="bg-blue-100/50"
    >
      <span v-if="showPercentages">
        {{ hasWorks && isNumber(totalCosts.currentCertified / totalCosts.budget) ? `${$tn((totalCosts.currentCertified / totalCosts.budget) * 100)}%` : '' }}
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ hasWorks && isNumber(totalCosts.previous / totalCosts.budget) ? `${$tn((totalCosts.previous / totalCosts.budget) * 100)}%` : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ hasWorks && isNumber(totalCosts.cumulative / totalCosts.budget) ? `${$tn((totalCosts.cumulative / totalCosts.budget) * 100)}%` : '' }}
    </td>

    <td>
      {{ !falsey(costToComplete) ? $tcc(costToComplete) : '' }}
    </td>
  </tr>

  <template v-if="open">
    <TradeRowItem
      v-for="tradeItem in tradeItems"
      :key="tradeItem.id"
      :trade="$props.trade"
      :claim="$props.claim"
      :trade-item="tradeItem"
      :show-percentages="$props.showPercentages"
      :show-subcontract-summary="$props.showSubcontractSummary"
      :lump-sum-cost-ids-for-claim-period="$props.lumpSumCostIdsForClaimPeriod"
      :rates-cost-ids-for-claim-period="$props.ratesCostIdsForClaimPeriod"
    />
  </template>
</template>

<script setup lang="ts">
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { falsey, isNumber } from '@/helpers';
import { t } from '@/i18n';
import Claim, { ClaimStatus } from '@/models/Claim';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useCommentsStore } from '@/store/comments';
import { useTradeItemsStore } from '@/store/tradeItems';
import { useTradesStore } from '@/store/trades';
import { useUserStore } from '@/store/user';
import Swal from 'sweetalert2';
import { computed, ref } from 'vue';
import { toast } from 'vue3-toastify';
import TradeRowItem from './TradeRowItem.vue';

const props = defineProps<{
  trade: Trade;
  claim: Claim;
  showPercentages?: boolean;
  showSubcontractSummary?: boolean;
  lumpSumCostIdsForClaimPeriod: Id[];
  ratesCostIdsForClaimPeriod: Id[];
}>();

const tradeItemsStore = useTradeItemsStore();

const tradeItems = computed(() => {
  return tradeItemsStore.models
    .where('trade_id', props.trade.id)
    .whereNull('parent_id')
    .with('trade_item_costs', (query) => {
      query.where('claim_id', props.claim.id);
    }).orderBy('sort_order')
    .get();
});

const collectLeafTradeItems = (tradeItem: TradeItem, leaves: TradeItem[] = []) => {
  if(tradeItem.has_children) {
    tradeItemsStore.models
      .where('parent_id', tradeItem.id)
      .orderBy('sort_order')
      .get()
      .forEach((child) => collectLeafTradeItems(child, leaves));
  } else {
    leaves.push(tradeItem);
  }

  return leaves;
};

const leafTradeItems = computed(() => {
  const leaves: TradeItem[] = [];

  tradeItems.value.forEach((tradeItem) => collectLeafTradeItems(tradeItem, leaves));

  return leaves;
});

const hasWorks = computed(() => leafTradeItems.value.some((leaf) => leaf.budget >= 0));

const totalCosts = computed(() => {
  const totals = {
    budget: 0,
    adjustedBudget: 0,
    currentClaim: 0,
    currentCertified: 0,
    previous: 0,
    cumulative: 0,
    variance: 0,
    subcontractClaim: 0,
    subcontractCertified: 0,
  };

  tradeItems.value.forEach((tradeItem) => {
    totals.budget += tradeItem.childBudget();
    totals.adjustedBudget += tradeItem.childAdjustedBudget();
    totals.previous += tradeItem.childPrevious(props.claim);
    totals.currentClaim += tradeItem.childClaim(props.claim);
    totals.currentCertified += tradeItem.childCertified(props.claim);
    totals.variance += tradeItem.childVariance(props.claim);

    totals.subcontractClaim += tradeItem.childSubcontractSummaryClaim(
      props.lumpSumCostIdsForClaimPeriod,
      props.ratesCostIdsForClaimPeriod,
    );

    totals.subcontractCertified += tradeItem.childSubcontractSummaryCertified(
      props.lumpSumCostIdsForClaimPeriod,
      props.ratesCostIdsForClaimPeriod,
    );
  });

  totals.cumulative = totals.previous + totals.currentCertified;

  return totals;
});

const costToComplete = computed(() => {
  if(!hasWorks.value) {
    return undefined;
  }

  return Math.abs(totalCosts.value.budget - totalCosts.value.cumulative);
});

const open = ref(false);
const commentsStore = useCommentsStore();
const tradesStore = useTradesStore();
const userStore = useUserStore();
const updateCommentAction = useStoreApiAction(tradesStore.updateComment);
const isReadOnly = userStore.isClientManager();

const canEditCertified = computed(() => {
  return !isReadOnly && props.claim.status === ClaimStatus.draft;
});

const varianceComment = computed(() => {
  return commentsStore.models
    .where('commentable_id', props.trade.id)
    .where('commentable_type', props.trade.$entity())
    .where('creator_id', props.claim.id)
    .where('creator_type', props.claim.$entity())
    .first();
});

const hasVariance = computed(() => {
  return !falsey(totalCosts.value?.currentClaim) && !falsey(totalCosts.value?.currentCertified);
});

const variance = computed(() => {
  return totalCosts.value?.variance;
});

const showCommentButton = computed(() => {
  return hasVariance.value && variance.value !== 0 && !!varianceComment.value && canEditCertified.value;
});

const editVarianceComment = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentTitle'),
    text: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentText'),
    input: 'textarea',
    inputValue: varianceComment.value?.body ?? '',
    showCancelButton: true,
    confirmButtonText: 'Update',
  }).then((result) => {
    if(result.isConfirmed) {
      if(result.value) {
        updateCommentAction.request(props.trade.id, varianceComment.value.id, { comment: result.value }).then(() => {
          toast.success('Comment updated');
        });
      }
    }
  });
};
</script>

<style scoped></style>
